import setContextualFormatBarTarget from 'owa-editor-ribbonplugin-store/lib/actions/setContextualFormatBarTarget';
import {
    lazyFormatPainterFinish,
    lazyShowInsertLinkDialog,
    ContextualFormatBar,
    InsertLinkSource,
} from '../index';
import updateRibbonState from 'owa-editor-ribbonplugin-store/lib/actions/updateRibbonState';
import type RibbonViewState from 'owa-editor-ribbonplugin-store/lib/store/schema/RibbonViewState';
import type { EditorViewState, PluginViewStateAdapter, PluginWithUI } from 'owa-editor';
import { isMac } from 'owa-user-agent/lib/userAgent';
import {
    ContentEditFeature,
    ContextMenuProvider,
    IEditor,
    PluginEvent,
    PluginEventType,
} from 'roosterjs-editor-types';
import { owaDate } from 'owa-datetime';
import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import safeInstanceOf from 'roosterjs-editor-dom/lib/utils/safeInstanceOf';
import { imageContextAddAltText } from './RibbonPlugin.locstring.json';
import { lazyShowImageAltTextDialog } from 'owa-editor-ribbon-secondary-ui';
import loc from 'owa-localize';
import type PluginUtils from 'owa-editor-plugin-utils/lib/schema/PluginUtils';
import type EditorPluginEx from 'owa-editor-plugin-utils/lib/schema/EditorPluginEx';
import { IPoint } from '@fluentui/react/lib/Utilities';

const KEY_ESC = 27;
const KEY_K = 75;

const DISPLAY_CONTEXTUAL_FORMATBAR_POLL_INTERVAL: number = 50;

const hasCtrlOrMetaKey: (event: KeyboardEvent) => boolean = isMac()
    ? e => e.metaKey
    : e => e.ctrlKey;

export default class RibbonPlugin
    implements
        PluginWithUI<RibbonViewState>,
        ContextMenuProvider<IContextualMenuItem>,
        EditorPluginEx {
    private editor: IEditor;
    private pluginUtils: PluginUtils;
    private displayContextFormatBarJobId: number;

    constructor(
        public readonly adapter: PluginViewStateAdapter<RibbonViewState>,
        public readonly editorAdapter: PluginViewStateAdapter<EditorViewState>,
        public allowCtrlK: boolean = true
    ) {}

    public getName() {
        return 'Ribbon';
    }

    public initialize(editor: IEditor): void {
        this.editor = editor;
        if (this.allowCtrlK) {
            this.editor.addContentEditFeature(this.insertLinkFeature);
        }
        this.editor.addContentEditFeature(this.closeContextualFormatBarFeature);
    }

    public onPluginEvent(event: PluginEvent): void {
        const ribbonViewState = this.adapter();

        if (
            (event.eventType == PluginEventType.KeyUp && event.rawEvent.keyCode != KEY_ESC) ||
            event.eventType == PluginEventType.MouseUp ||
            event.eventType == PluginEventType.ContentChanged
        ) {
            let updateContextFormatBar = event.eventType != PluginEventType.ContentChanged;

            updateRibbonState(this.editorAdapter(), ribbonViewState, true /* delay */);

            if (updateContextFormatBar) {
                this.displayContextFormatBar(ribbonViewState);
            }
        }

        if (
            event.eventType == PluginEventType.MouseUp &&
            ribbonViewState.formatViewState.formatPainterFormat
        ) {
            // NOTE: When the editor is disposed, we trigger the plugin event "BeforeDispose",
            // so check the event type first (VSO 54192)
            lazyFormatPainterFinish
                .import()
                .then(formatPainterFinish =>
                    formatPainterFinish(this.editorAdapter(), this.adapter().formatViewState)
                );
        }
    }

    public getContextMenuItems(image: Node) {
        return safeInstanceOf(image, 'HTMLImageElement')
            ? [
                  {
                      name: loc(imageContextAddAltText),
                      key: 'addAltText',
                      onClick: () => {
                          this.onAltText(image);
                      },
                  },
              ]
            : null;
    }

    public dispose(): void {
        this.editor = null;
    }

    public getUIComponentClass() {
        return ContextualFormatBar;
    }

    public isDarkMode() {
        return this.editor?.isDarkMode();
    }

    public setPluginUtils(pluginUtils: PluginUtils) {
        this.pluginUtils = pluginUtils;
    }

    private onAltText(image: HTMLImageElement) {
        lazyShowImageAltTextDialog.import().then(showImageAltTextDialog => {
            // Add null check in the case of slow network.
            // Image is deleted before dialog is imported.
            if (this.editor?.contains(image)) {
                showImageAltTextDialog(image, this.editor.getDocument().defaultView!, alt => {
                    image.alt = alt;
                });
            }
        });
    }

    private insertLinkFeature: ContentEditFeature = {
        keys: [KEY_K],
        allowFunctionKeys: true,
        shouldHandleEvent: event => {
            const rawEvent = event.rawEvent;
            return !rawEvent.altKey && !rawEvent.shiftKey && hasCtrlOrMetaKey(rawEvent);
        },
        handleEvent: event => {
            const rawEvent = event.rawEvent;
            rawEvent.preventDefault();
            rawEvent.stopPropagation();
            lazyShowInsertLinkDialog
                .import()
                .then(showInsertLinkDialog =>
                    showInsertLinkDialog(
                        this.editorAdapter(),
                        this.editor.getDocument().defaultView,
                        InsertLinkSource.ShortKey,
                        owaDate()
                    )
                );
        },
    };

    private closeContextualFormatBarFeature: ContentEditFeature = {
        keys: [KEY_ESC],
        shouldHandleEvent: () => !!this.adapter().targetPoint,
        handleEvent: event => {
            const rawEvent = event.rawEvent;
            rawEvent.preventDefault();
            rawEvent.stopPropagation();
            setContextualFormatBarTarget(this.adapter(), null /*targetPoint*/);
        },
    };

    private displayContextFormatBar(ribbonViewState: RibbonViewState) {
        if (this.displayContextFormatBarJobId) {
            window.clearTimeout(this.displayContextFormatBarJobId);
        }

        this.displayContextFormatBarJobId = window.setTimeout(() => {
            let cursorPosition: IPoint = null;
            if (this.editor?.hasFocus()) {
                const range = this.editor.getSelectionRange();
                if (range && !range.collapsed) {
                    const focusedPosition = this.editor.getFocusedPosition();
                    const rect =
                        focusedPosition && this.pluginUtils.getPositionRect(focusedPosition);
                    cursorPosition = rect && {
                        x: rect.left,
                        y: rect.top,
                    };
                }
            }
            setContextualFormatBarTarget(ribbonViewState, cursorPosition);
        }, DISPLAY_CONTEXTUAL_FORMATBAR_POLL_INTERVAL);
    }
}
